import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { usePlayer } from '../context/PlayerContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { UserPlus, UserCheck } from 'lucide-react';
import { 
  User, 
  CheckCircle, 
  Music, 
  Play, 
  Pause,
  Calendar,
  ExternalLink,
  Loader2,
  Globe,
  Link as LinkIcon,
  Share2,
  Copy,
  Mail,
  Phone,
  Youtube,
  Instagram,
  Twitter,
  Facebook,
  Music2,
  MessageCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for different link types
const linkIcons = {
  spotify: Music2,
  youtube: Youtube,
  youtube_music: Youtube,
  instagram: Instagram,
  twitter: Twitter,
  facebook: Facebook,
  website: Globe,
  custom: LinkIcon
};

// Extract YouTube video ID from various URL formats
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const ArtistProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user: currentUser, token } = useAuth();
  const { currentSong, isPlaying, playSong, pauseSong } = usePlayer();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/users/${username}`, { headers });
      setProfileData(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFollow = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to follow');
      navigate('/login');
      return;
    }
    if (!profileData?.user) return;
    setFollowLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (profileData.is_following) {
        await axios.delete(`${API}/users/${profileData.user.id}/follow`, { headers });
        setProfileData({
          ...profileData,
          is_following: false,
          followers_count: Math.max(0, (profileData.followers_count || 0) - 1),
        });
        toast.success(`Unfollowed ${profileData.user.full_name}`);
      } else {
        await axios.post(`${API}/users/${profileData.user.id}/follow`, {}, { headers });
        setProfileData({
          ...profileData,
          is_following: true,
          followers_count: (profileData.followers_count || 0) + 1,
        });
        toast.success(`Following ${profileData.user.full_name}`);
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const profileUrl = `${window.location.origin}/${username}`;
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(profileUrl);
    toast.success('Link copied to clipboard!');
  };

  const shareLinks = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-green-600',
      url: `https://wa.me/?text=Check out ${profileData?.user?.full_name} on M4manna! ${encodeURIComponent(profileUrl)}`
    },
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'bg-sky-500',
      url: `https://twitter.com/intent/tweet?text=Check out ${profileData?.user?.full_name} on M4manna!&url=${encodeURIComponent(profileUrl)}`
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-blue-600',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'bg-neutral-600',
      url: `mailto:?subject=Check out ${profileData?.user?.full_name} on M4manna&body=${encodeURIComponent(profileUrl)}`
    }
  ];

  const handlePlaySong = (song, allSongs) => {
    if (currentSong?.id === song.id && isPlaying) {
      pauseSong();
    } else {
      playSong(song, allSongs);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-m4-yellow" />
        </div>
      </Layout>
    );
  }

  if (!profileData?.user) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <User className="h-16 w-16 mx-auto mb-4 text-neutral-700" />
            <h2 className="font-heading text-2xl font-bold text-white">Profile Not Found</h2>
            <p className="text-neutral-400 mt-2">The user @{username} doesn't exist.</p>
            <Link to="/discover" className="text-m4-yellow hover:underline mt-4 inline-block">
              Browse Music
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const { user, songs, bio_link } = profileData;
  const isArtist = user.role === 'artist' || user.role === 'mentor';
  const isOwnProfile = currentUser?.id === user.id;

  // Get YouTube videos from bio_link
  const youtubeVideos = bio_link?.youtube_videos || [];
  
  // Get songs with YouTube links
  const songsWithYoutube = songs.filter(s => s.youtube_url && getYouTubeVideoId(s.youtube_url));

  return (
    <Layout>
      {/* Bio.Link Style Profile Page */}
      <div className="min-h-screen bg-gradient-to-b from-neutral-900 via-black to-black">
        {/* Header Background */}
        <div className="relative h-48 md:h-64">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: bio_link?.cover_image 
                ? `url(${bio_link.cover_image})`
                : `url('https://images.unsplash.com/photo-1696946908910-086370120eed?w=1920')`,
              opacity: 0.3
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black" />
        </div>

        <div className="container mx-auto max-w-2xl px-4 -mt-24 relative z-10">
          {/* Profile Header */}
          <div className="text-center mb-8">
            {/* Avatar */}
            <div className="h-32 w-32 mx-auto rounded-full bg-m4-yellow border-4 border-black flex items-center justify-center overflow-hidden shadow-xl">
              {user.profile_image ? (
                <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-5xl font-heading font-bold text-black">
                  {user.full_name?.charAt(0)}
                </span>
              )}
            </div>

            {/* Name & Badge */}
            <div className="mt-4">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white flex items-center justify-center gap-2">
                {user.full_name}
                {user.is_verified && (
                  <CheckCircle className="h-6 w-6 text-m4-yellow" fill="#FFE500" />
                )}
              </h1>
              <p className="text-neutral-400 mt-1">@{user.username}</p>
            </div>

            {/* Biography */}
            {(bio_link?.biography || user.bio) && (
              <p className="text-neutral-300 mt-4 max-w-md mx-auto leading-relaxed">
                {bio_link?.biography || user.bio}
              </p>
            )}

            {/* Stats */}
            {isArtist && (
              <div className="flex justify-center gap-8 mt-6">
                <div className="text-center">
                  <p className="font-heading text-2xl font-bold text-m4-yellow">{songs.length}</p>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Songs</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-2xl font-bold text-m4-yellow">
                    {songs.reduce((acc, s) => acc + (s.play_count || 0), 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Plays</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-2xl font-bold text-m4-yellow" data-testid="followers-count">
                    {(profileData?.followers_count || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Followers</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-center gap-3 mt-6 flex-wrap">
              {/* Follow Button */}
              {!isOwnProfile && (
                <Button
                  onClick={handleToggleFollow}
                  disabled={followLoading}
                  className={
                    profileData?.is_following
                      ? 'bg-transparent border border-[#FFE500] text-[#FFE500] hover:bg-[#FFE500]/10 rounded-full px-5 font-semibold'
                      : 'bg-[#FFE500] text-black hover:bg-[#F0D800] rounded-full px-5 font-bold'
                  }
                  data-testid="follow-btn"
                >
                  {profileData?.is_following ? (
                    <><UserCheck className="h-4 w-4 mr-2" /> Following</>
                  ) : (
                    <><UserPlus className="h-4 w-4 mr-2" /> Follow</>
                  )}
                </Button>
              )}

              {/* Share Button */}
              <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-neutral-700 text-white hover:bg-neutral-800"
                    data-testid="share-profile-btn"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-neutral-900 border-neutral-800 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-white">Share Profile</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {/* Copy Link */}
                    <div className="flex items-center gap-2 bg-neutral-800 p-3 rounded-xl">
                      <input 
                        type="text" 
                        value={profileUrl} 
                        readOnly 
                        className="flex-1 bg-transparent text-white text-sm outline-none"
                      />
                      <Button 
                        size="sm" 
                        onClick={handleCopyLink}
                        className="bg-m4-yellow text-black hover:bg-m4-yellow/90"
                        data-testid="copy-link-btn"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Social Share Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      {shareLinks.map((platform) => {
                        const Icon = platform.icon;
                        return (
                          <a
                            key={platform.name}
                            href={platform.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 ${platform.color} text-white p-3 rounded-xl hover:opacity-90 transition-opacity`}
                            data-testid={`share-${platform.name.toLowerCase()}`}
                          >
                            <Icon className="h-5 w-5" />
                            <span className="font-medium">{platform.name}</span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Book Session */}
              {isArtist && !isOwnProfile && (
                <Link to={`/book/${user.username}`}>
                  <Button className="bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold">
                    <Calendar className="h-4 w-4 mr-2" />
                    Book Session
                  </Button>
                </Link>
              )}

              {/* Edit Profile (Own) */}
              {isOwnProfile && (
                <Link to="/bio-link">
                  <Button className="bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold">
                    Edit Profile
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Contact Buttons (Visible for logged-in users) */}
          {isAuthenticated && (bio_link?.contact_email || bio_link?.contact_phone) && (
            <div className="flex justify-center gap-3 mb-8">
              {bio_link?.contact_email && (
                <a 
                  href={`mailto:${bio_link.contact_email}`}
                  className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 px-4 py-2 rounded-full text-white hover:border-m4-yellow transition-colors"
                  data-testid="contact-email-btn"
                >
                  <Mail className="h-4 w-4 text-m4-yellow" />
                  Email
                </a>
              )}
              {bio_link?.contact_phone && (
                <a 
                  href={`tel:${bio_link.contact_phone}`}
                  className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 px-4 py-2 rounded-full text-white hover:border-m4-yellow transition-colors"
                  data-testid="contact-phone-btn"
                >
                  <Phone className="h-4 w-4 text-m4-yellow" />
                  Call
                </a>
              )}
            </div>
          )}

          {/* Social Links */}
          {bio_link?.links?.length > 0 && (
            <div className="space-y-3 mb-8">
              {bio_link.links.map((link, index) => {
                const IconComponent = linkIcons[link.type] || linkIcons.custom;
                return (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 bg-neutral-900 border border-neutral-800 p-4 hover:border-m4-yellow hover:bg-neutral-800/50 transition-all group"
                    data-testid={`bio-link-${index}`}
                  >
                    <div className="h-10 w-10 bg-m4-yellow/10 flex items-center justify-center group-hover:bg-m4-yellow/20 transition-colors">
                      <IconComponent className="h-5 w-5 text-m4-yellow" />
                    </div>
                    <span className="flex-1 font-medium text-white">{link.title}</span>
                    <ExternalLink className="h-4 w-4 text-neutral-500 group-hover:text-m4-yellow transition-colors" />
                  </a>
                );
              })}
            </div>
          )}

          {/* YouTube Videos Section */}
          {(youtubeVideos.length > 0 || songsWithYoutube.length > 0) && (
            <div className="mb-8">
              <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Youtube className="h-5 w-5 text-red-500" />
                Videos
              </h2>
              <div className="space-y-4">
                {/* Embedded YouTube Videos from bio_link */}
                {youtubeVideos.map((video, index) => {
                  const videoId = getYouTubeVideoId(video.url);
                  if (!videoId) return null;
                  return (
                    <div key={index} className="bg-neutral-900 border border-neutral-800 overflow-hidden">
                      {video.title && (
                        <p className="p-3 font-medium text-white border-b border-neutral-800">{video.title}</p>
                      )}
                      <div className="aspect-video">
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title={video.title || `Video ${index + 1}`}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  );
                })}

                {/* Songs with YouTube Links */}
                {songsWithYoutube.map((song) => {
                  const videoId = getYouTubeVideoId(song.youtube_url);
                  if (!videoId) return null;
                  return (
                    <div key={song.id} className="bg-neutral-900 border border-neutral-800 overflow-hidden">
                      <p className="p-3 font-medium text-white border-b border-neutral-800 flex items-center gap-2">
                        <Music className="h-4 w-4 text-m4-yellow" />
                        {song.title}
                      </p>
                      <div className="aspect-video">
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title={song.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Music Section */}
          {songs.length > 0 && (
            <div className="mb-8">
              <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Music className="h-5 w-5 text-m4-yellow" />
                Music
              </h2>
              <div className="space-y-2">
                {songs.map((song) => {
                  const isCurrentPlaying = currentSong?.id === song.id && isPlaying;
                  const hasAudio = song.audio_url || song.youtube_url;
                  
                  return (
                    <div 
                      key={song.id}
                      className="flex items-center gap-4 bg-neutral-900 border border-neutral-800 p-3 hover:border-m4-yellow/50 transition-colors group"
                      data-testid={`song-item-${song.id}`}
                    >
                      {/* Cover */}
                      <div className="relative h-14 w-14 bg-neutral-800 flex-shrink-0 overflow-hidden">
                        <img 
                          src={song.cover_image || 'https://images.unsplash.com/photo-1581650127213-e72e2271ff15?w=100'}
                          alt={song.title}
                          className="w-full h-full object-cover"
                        />
                        {hasAudio && (
                          <button
                            onClick={() => handlePlaySong(song, songs)}
                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                          >
                            {isCurrentPlaying ? (
                              <Pause className="h-6 w-6 text-m4-yellow" fill="#FFE500" />
                            ) : (
                              <Play className="h-6 w-6 text-m4-yellow ml-0.5" fill="#FFE500" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`/song/${song.id}`}
                          className="font-medium text-white hover:text-m4-yellow transition-colors block truncate"
                        >
                          {song.title}
                        </Link>
                        <p className="text-sm text-neutral-500">{song.genre}</p>
                      </div>

                      {/* Play count */}
                      <div className="text-right text-sm text-neutral-500">
                        <div className="flex items-center gap-1">
                          <Play className="h-3 w-3" />
                          {song.play_count?.toLocaleString() || 0}
                        </div>
                      </div>

                      {/* YouTube indicator */}
                      {song.youtube_url && (
                        <a 
                          href={song.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-500 hover:text-red-400"
                        >
                          <Youtube className="h-5 w-5" />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {isArtist && songs.length === 0 && !bio_link?.links?.length && (
            <div className="text-center py-12 bg-neutral-900/50 border border-neutral-800">
              <Music className="h-12 w-12 mx-auto mb-3 text-neutral-700" />
              <p className="text-neutral-400">This artist hasn't added any content yet</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-center py-8 border-t border-neutral-800 mt-8">
            <p className="text-xs text-neutral-500">
              Powered by <span className="text-m4-yellow font-bold">M4manna</span>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ArtistProfile;
